import {
  PersonOutlined,
  Person,
  ForwardToInboxOutlined,
  ForwardToInbox,
  HowToReg,
  HowToRegOutlined,
  PlaylistAdd,
  PlaylistAddCheck,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';

/** Role + context slot invite/accept (flow details: session actors or flow moderators). */
export type FlowDetailsInviteState = {
  roleAccepted: boolean;
  roleInvited: boolean;
  /** Staged for batch invite from contributor list / widget OUTBOX. */
  roleStagedForInvite: boolean;
  slotInvited: boolean;
  slotAccepted: boolean;
  slotStagedForInvite: boolean;
};

type ContributorInvitationIconProps = {
  contributor: ContributorDto;
  /** When set (flow details with slot context), icon colors follow role + slot rules; otherwise contributor-level only. */
  flowDetailsInvite?: FlowDetailsInviteState;
};

/** MUI palette keys for avatar ring / emphasis (contributor-level invite, not profile review). */
export type ContributorInvitationRingColor = 'default' | 'info' | 'success';

/** Ring on contributor avatar: invited/waiting → info, accepted → success, else no ring. */
export function getContributorInvitationRingColor(contributor: ContributorDto): ContributorInvitationRingColor {
  if (contributor.acceptedAt) {
    return 'success';
  }
  if (contributor.invitedAt) {
    return 'info';
  }
  return 'default';
}

export const ContributorInvitationIcon = ({ contributor, flowDetailsInvite }: ContributorInvitationIconProps) => {
  const { t } = useTranslation();

  if (flowDetailsInvite) {
    const { roleAccepted, roleInvited, roleStagedForInvite, slotInvited, slotAccepted, slotStagedForInvite } =
      flowDetailsInvite;

    const rs = roleStagedForInvite === true;
    const ss = slotStagedForInvite === true;

    // 1. Role and slot accepted
    if (roleAccepted && slotAccepted) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsBothAccepted')}>
          <HowToRegOutlined color="success" />
        </Tooltip>
      );
    }

    // 2. Slot invited, not yet accepted
    if (slotInvited && !slotAccepted) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsSlotInvitationPending')}>
          <ForwardToInbox color="info" />
        </Tooltip>
      );
    }

    // 3. Role invitation pending, time slot on invite list (staged)
    if (roleInvited && !roleAccepted && ss) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsRolePendingSlotStaged')}>
          <PlaylistAddCheck color="info" />
        </Tooltip>
      );
    }

    // 4. Role accepted; time slot not on invite list and not invited
    if (roleAccepted && !ss && !slotInvited) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsRoleAcceptedSlotNotInvited')}>
          <HowToReg color="primary" />
        </Tooltip>
      );
    }

    // 5. Role + slot on invite list, or role accepted + slot on invite list
    if ((rs && ss) || (roleAccepted && ss)) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsRoleAndSlotOnInviteList')}>
          <PlaylistAddCheck color="primary" />
        </Tooltip>
      );
    }

    // 6. Only role on invite list (slot not)
    if (rs && !ss) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsRoleOnlyOnInviteList')}>
          <PlaylistAdd color="primary" />
        </Tooltip>
      );
    }

    // 7. Neither role nor slot staged or invited
    if (!rs && !roleInvited && !ss && !slotInvited) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsNotStagedNotInvited')}>
          <Person color="primary" />
        </Tooltip>
      );
    }

    // 8. Fallback: invitation pending (role and/or slot)
    if ((roleInvited && !roleAccepted) || (slotInvited && !slotAccepted)) {
      return (
        <Tooltip title={t('contributor.invitationStatus.flowDetailsPendingRoleOrSlotInvitation')}>
          <ForwardToInboxOutlined color="info" />
        </Tooltip>
      );
    }

    return null;
  }

  if (contributor.acceptedAt) {
    return (
      <Tooltip title={t('contributor.invitationStatus.accepted')}>
        <HowToRegOutlined />
      </Tooltip>
    );
  }
  if (!contributor.invitedAt) {
    if (contributor.email === undefined) {
      return (
        <Tooltip title={t('contributor.invitationStatus.draft')}>
          <PersonOutlined />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={t('contributor.invitationStatus.toBeInvited')}>
        <Person />
      </Tooltip>
    );
  }

  if (contributor.invitedAt) {
    return (
      <Tooltip title={t('contributor.invitationStatus.invited')}>
        <ForwardToInboxOutlined />
      </Tooltip>
    );
  }

  return null;
};
